import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { CollectionEventDependencies } from '../CollectionEventController';
import { CollectEventProgressBarMainMenuLayout } from '../../../../layouts/collectEvent/CollectEventProgressBarMainMenuLayout';
import type { CollectionEventConfig, CollectionEventRewardProgress } from '../types';
import { preloadCollectionEventAssets } from './preloadCollectionEventAssets';
import { CollectionTokenIconView } from './CollectionTokenIconView';
import {
  getCollectionEventRewardProgress,
  getCurrentEventInstance,
  getRewardConfig,
} from '../model/collectionEventUtils';
import { setTextAsTimerCountdown } from '../../../utils/pixi/setTextAsTimerCountdown';
import { createInventoryItemViews } from '../../../views/InventoryItemAmountView';
import type { ResourceMap } from '../../../app/resources/types';
import type { EasingType } from '../../../libs/TweenManager/easing';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { setContainerParent } from '../../../utils/pixi/transform-utils';
import { getTimeLimitHandler } from '../../../libs/timeLimit/getTimeLimitHandler';

export class CollectionEventProgressBarView extends Container {
  public readonly onClicked = new Signal();

  private readonly onViewChange = new Signal<(visible: boolean) => void>();

  private layout: CollectEventProgressBarMainMenuLayout | null = null;

  private rewardView: Container | null = null;

  private currentEventId: string | null = null;

  private initProgress: CollectionEventRewardProgress | null = null;

  constructor(private readonly dependencies: CollectionEventDependencies) {
    super();
    this.init();
  }

  public addVisibilityChangeListener(callback: (visible: boolean) => void): void {
    callback(!!this.layout);
    this.onViewChange.connect(callback);
  }

  private init(): void {
    this.dependencies.observable.addChangeHandler(this, async (data) => {
      const { collectionEvent } = data;
      if (collectionEvent.currentEvent === null) return;

      if (this.currentEventId === null && collectionEvent.currentEvent) {
        this.currentEventId = collectionEvent.currentEvent.id;
        void this.createView(collectionEvent.currentEvent);
        return;
      }

      // event changed
      if (collectionEvent.currentEvent && collectionEvent.currentEvent.id !== this.currentEventId) {
        this.currentEventId = collectionEvent.currentEvent.id;
        this.layout?.root.tweenTo({ alpha: 0 }, 100, 'sinOut', true);
        void this.createView(collectionEvent.currentEvent);
      }
    });
  }

  private async createView(eventInstance: CollectionEventConfig): Promise<void> {
    await preloadCollectionEventAssets(eventInstance.visuals);

    if (this.destroyed) return;

    this.layout = new CollectEventProgressBarMainMenuLayout();

    this.onViewChange.emit(true);

    const { tokenSlot, progressBarFill, progressBarEmpty, progressText, root, progressBarWrapper } = this.layout;

    root.props({
      parent: this,
    });

    progressBarWrapper.on('pointertap', () => {
      this.onClicked.emit();
    });

    new CollectionTokenIconView(eventInstance.visuals).props({
      parent: tokenSlot,
    });

    const progress = getCollectionEventRewardProgress(eventInstance, eventInstance.tokensLastSeen);

    this.initProgress = progress;

    progressBarFill.width = Math.round(progressBarEmpty.width * (progress.progress / progress.target));
    progressText.text = `${progress.progress}/${progress.target}`;

    setTextAsTimerCountdown(this.layout.timerText, getTimeLimitHandler(eventInstance.timeLimit).getEndDate());
    this.rewardView = this.createRewardView(progress.rewards).props({
      parent: this.layout.rewardSlot,
    });
  }

  private createRewardView(resourceMap: ResourceMap): Container {
    const root = new Container();
    createInventoryItemViews(resourceMap).forEach((view) => {
      view.props({ parent: root });
    });
    return root;
  }

  public async playProgressAnimation(reward: CollectionEventRewardProgress): Promise<void> {
    const { initProgress, layout } = this;

    if (!initProgress || !layout) {
      await this.wait(500);
      await this.playProgressAnimation(reward);
      return;
    }

    if (this.destroyed) return;

    const originalParent = this.parent;

    if (!originalParent) return;

    originalParent.on('destroyed', () => {
      this.destroy();
    });

    setContainerParent(this, this.pixi.stage);

    const eventInstance = getCurrentEventInstance(this.dependencies);
    if (!eventInstance) return;

    const token = new CollectionTokenIconView(eventInstance.visuals)
      .showAmount(eventInstance.tokensCollected - eventInstance.tokensLastSeen)
      .props({ parent: layout.rewardAnimationSlot });

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const { promise, resolve } = Promise.withResolvers<void>();

    void playLayoutAnimation(layout, 'Collect_Animation', false, async (eventName) => {
      if (eventName === 'startProgressBarAnimation') {
        if (initProgress.rewardIndex === reward.rewardIndex) {
          await this.animateProgressBar(initProgress, reward.progress, 500, 'sinOut');
        } else {
          await this.animateWithRewardFlow(eventInstance, initProgress, reward);
        }
        resolve();
      }
    });

    await promise;

    if (this.destroyed) return;

    token.destroy({ children: true });

    // CAREFUL - it scales overlay to 0, effectively turning off input lock
    void playLayoutAnimation(layout, 'Collect_Animation_Finished', false);

    setContainerParent(this, originalParent);
  }

  private async animateWithRewardFlow(
    eventInstance: CollectionEventConfig,
    initProgress: CollectionEventRewardProgress,
    currentProgress: CollectionEventRewardProgress,
  ): Promise<void> {
    if (this.destroyed) return;

    const progressSteps: {
      initProgress: number;
      progress: number;
      target: number;
      rewardToSwitch: ResourceMap | null;
    }[] = [];

    for (let { rewardIndex } = initProgress; rewardIndex < currentProgress.rewardIndex; rewardIndex += 1) {
      const target = getRewardConfig(eventInstance, rewardIndex).tokensReq ?? initProgress.target;
      progressSteps.push({
        initProgress: rewardIndex === initProgress.rewardIndex ? initProgress.progress : 0,
        progress: target,
        target,
        rewardToSwitch: getRewardConfig(eventInstance, rewardIndex + 1).rewards,
      });
    }

    progressSteps.push({
      initProgress: 0,
      progress: currentProgress.progress,
      target: currentProgress.target,
      rewardToSwitch: null,
    });

    // eslint-disable-next-line no-restricted-syntax
    for (const step of progressSteps) {
      if (this.destroyed) return;
      await this.animateProgressBar(
        {
          progress: step.initProgress,
          target: step.target,
        },
        step.progress,
        1000,
        'linear',
      );
      if (step.rewardToSwitch) {
        await this.animateRewardChange(step.rewardToSwitch);
      }
    }
  }

  private async animateRewardChange(newReward: ResourceMap): Promise<void> {
    if (!this.layout) return;
    if (this.destroyed) return;

    this.rewardView?.tweenTo({ y: -50, scaleXY: 1.2, alpha: 0 }, 500, 'cubicOut', true);
    this.rewardView = this.createRewardView(newReward).props({
      parent: this.layout.rewardSlot,
    });
    this.rewardView.tweenFrom({ scaleXY: 0 }, 500, 'appear1', true);
  }

  private async animateProgressBar(
    fromProgress: { progress: number; target: number },
    toProgress: number,
    duration: number,
    easing: EasingType,
  ): Promise<void> {
    if (this.destroyed) return;
    if (!this.layout) return;

    const { progressBarFill, progressBarEmpty, progressText } = this.layout;

    const tween = progressBarFill.tweens.createValueTween(
      fromProgress.progress,
      toProgress,
      duration,
      (value) => {
        progressBarFill.width = Math.round(progressBarEmpty.width * (value / fromProgress.target));
        progressText.text = `${Math.round(value)}/${fromProgress.target}`;
      },
      easing,
    );

    await tween.asyncOnComplete();
  }
}
